/**
 * Service für Tutorial-Videos (Hilfe-Bereich + Signup-Popup).
 * Published Videos lesbar für alle Authentifizierten; Admins verwalten alles.
 * Read-State in `tutorial_video_reads` (Insert-only via RLS, wie update_reads).
 */
import { supabase } from '@/lib/supabase';
import { TutorialVideo } from '@/types';

interface TutorialRow {
  id: string;
  title: string;
  description: string | null;
  youtube_video_id: string;
  category: string | null;
  sort_order: number;
  show_on_signup: boolean;
  published: boolean;
  created_at: string;
}

function mapRow(row: TutorialRow): TutorialVideo {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    youtubeVideoId: row.youtube_video_id,
    category: row.category,
    sortOrder: row.sort_order,
    showOnSignup: row.show_on_signup,
    published: row.published,
    createdAt: row.created_at,
  };
}

// ── User-facing ─────────────────────────────────────────────────────────────

export async function fetchPublishedTutorials(): Promise<TutorialVideo[]> {
  const { data, error } = await supabase
    .from('tutorial_videos')
    .select('*')
    .eq('published', true)
    .order('sort_order', { ascending: true });
  if (error) { console.error('[Tutorials] fetchPublishedTutorials', error); throw error; }
  return (data ?? []).map(mapRow);
}

export async function fetchTutorialReadIds(): Promise<string[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('tutorial_video_reads')
    .select('video_id')
    .eq('user_id', user.id);
  if (error) { console.error('[Tutorials] fetchTutorialReadIds', error); throw error; }
  return (data ?? []).map((r: { video_id: string }) => r.video_id);
}

export async function markTutorialRead(videoId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const { error } = await supabase
    .from('tutorial_video_reads')
    // ignoreDuplicates ist load-bearing: keine UPDATE-Policy, echtes Upsert würde failen.
    .upsert(
      { user_id: user.id, video_id: videoId },
      { onConflict: 'user_id,video_id', ignoreDuplicates: true },
    );
  if (error) console.error('[Tutorials] markTutorialRead', error); // non-fatal
}

// ── Admin ───────────────────────────────────────────────────────────────────

export interface TutorialInput {
  title: string;
  description: string | null;
  youtubeVideoId: string;
  category: string | null;
  sortOrder: number;
  showOnSignup: boolean;
  published: boolean;
}

function toRow(input: TutorialInput) {
  return {
    title: input.title.trim(),
    description: input.description?.trim() || null,
    youtube_video_id: input.youtubeVideoId,
    category: input.category?.trim() || null,
    sort_order: input.sortOrder,
    show_on_signup: input.showOnSignup,
    published: input.published,
  };
}

export async function fetchAllTutorialsAdmin(): Promise<TutorialVideo[]> {
  const { data, error } = await supabase
    .from('tutorial_videos')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) { console.error('[Tutorials] fetchAllTutorialsAdmin', error); throw error; }
  return (data ?? []).map(mapRow);
}

export async function createTutorial(input: TutorialInput): Promise<void> {
  const { error } = await supabase.from('tutorial_videos').insert(toRow(input));
  if (error) { console.error('[Tutorials] createTutorial', error); throw error; }
}

export async function updateTutorial(id: string, input: TutorialInput): Promise<void> {
  const { error } = await supabase.from('tutorial_videos').update(toRow(input)).eq('id', id);
  if (error) { console.error('[Tutorials] updateTutorial', error); throw error; }
}

export async function deleteTutorial(id: string): Promise<void> {
  const { error } = await supabase.from('tutorial_videos').delete().eq('id', id);
  if (error) { console.error('[Tutorials] deleteTutorial', error); throw error; }
}
